import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, P, PhotoUploader } from "@/components/studio/portalKit";
import { CustomerFields, Section, SerialPicker, VehicleFields } from "@/components/studio/RegFields";
import { VelotintFilms, emptyFilm } from "@/components/studio/VelotintFilms";

const PPF = "Veloskin PPF";
const TINT = "Velotint";

export const emptyReg = () => ({
  customer: { name: "", mobile: "", email: "", address: "" },
  vehicle: { make: "", model: "", reg_no: "", colour: "", vin: "" },
  install_date: new Date().toISOString().slice(0, 10), installer_name: "",
  photos: { before: [], after: [], invoice: [] }, inspection_id: null,
  ppf: { product: "", coverage: "", coverage_panels: "", rolls: [{ product: "", serial_id: "", sqft: "" }] },
  films: [emptyFilm()],
  plan: { plan_type: "2-Year Protection Plan", payment_mode: "", payment_reference: "", payment_proof: [] },
  planBrand: PPF, on: { ppf: true, tint: false, plan: false },
});

// submission doc -> form (edit & resubmit one registration)
export const fromSubmission = (s) => {
  const f = emptyReg();
  const isTint = s.brand === TINT;
  const films = (s.films?.length ? s.films : [{ product: s.product, vlt: s.vlt, glass: s.glass || [], sqft: s.sqft, serial_id: s.serial_id }])
    .map((x) => ({ ...x, sqft: String(x.sqft ?? ""), serial_id: x.serial_id || "" }));
  return {
    ...f, customer: { ...f.customer, ...s.customer }, vehicle: { ...f.vehicle, ...s.vehicle },
    install_date: s.install_date, installer_name: s.installer_name, photos: { ...f.photos, ...s.photos },
    ppf: isTint ? f.ppf : { product: s.product, coverage: s.coverage, coverage_panels: s.coverage_panels || "",
      rolls: (s.rolls?.length ? s.rolls : [{ serial_id: s.serial_id, sqft: s.sqft }]).map((r) => ({ product: r.product || s.product || "", serial_id: r.serial_id || "", sqft: String(r.sqft ?? "") })) },
    films: isTint ? films : f.films,
    plan: { plan_type: s.plan_type || f.plan.plan_type, payment_mode: s.payment_mode || "", payment_reference: s.payment_reference || "", payment_proof: s.payment_proof || [] },
    planBrand: s.brand, on: { ppf: s.kind === "warranty" && !isTint, tint: s.kind === "warranty" && isTint, plan: s.kind === "plan" },
  };
};

const PpfFields = ({ f, setF, products, needSerial, credits }) => {
  const p = f.ppf;
  const set = (k, v) => setF({ ...f, ppf: { ...p, [k]: v } });
  const setRoll = (i, r) => set("rolls", p.rolls.map((x, n) => (n === i ? r : x)));
  const total = p.rolls.reduce((a, r) => a + Number(r.sqft || 0), 0);
  const ppfProducts = products.filter((x) => x.category === "PPF");
  return (
    <Section title="Veloskin PPF">
      <Field label="Coverage *">
        <Select value={p.coverage} onValueChange={(v) => set("coverage", v)}>
          <SelectTrigger data-testid="coverage-select"><SelectValue placeholder="Choose coverage" /></SelectTrigger>
          <SelectContent><SelectItem value="Full body">Full body</SelectItem><SelectItem value="Partial">Partial + panels</SelectItem></SelectContent>
        </Select>
      </Field>
      {p.coverage === "Partial" && <Field label="Panels covered"><Input data-testid="panels-input" value={p.coverage_panels} onChange={(e) => set("coverage_panels", e.target.value)} placeholder="Bonnet, front bumper, mirrors" /></Field>}
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">PPF used — one line per product / roll (e.g. Glossy 100 sqft, Matte 50 sqft)</p>
        {p.rolls.map((r, i) => (
          <div key={i} data-testid={`ppf-roll-row-${i}`} className="grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-[1fr_1fr_6rem_2.5rem] sm:items-end">
            <Field label="Product *">
              <Select value={r.product || ""} onValueChange={(v) => setRoll(i, { ...r, product: v, serial_id: "" })}>
                <SelectTrigger data-testid={`ppf-roll-product-${i}`}><SelectValue placeholder="Choose product" /></SelectTrigger>
                <SelectContent>{ppfProducts.map((x) => <SelectItem key={x.id} value={x.name}>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <SerialPicker brand={PPF} product={r.product} value={r.serial_id} required={needSerial} credit={credits[r.serial_id]}
              onChange={(v) => setRoll(i, { ...r, serial_id: v })} testid={`ppf-roll-serial-${i}`} />
            <Field label="Sqft *"><Input data-testid={`ppf-roll-sqft-${i}`} type="number" min="0" value={r.sqft} onChange={(e) => setRoll(i, { ...r, sqft: e.target.value })} /></Field>
            <Button type="button" size="icon" variant="ghost" className="h-10 w-10 justify-self-end" disabled={p.rolls.length === 1} aria-label="Remove line"
              data-testid={`ppf-roll-remove-${i}`} onClick={() => set("rolls", p.rolls.filter((_, n) => n !== i))}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
          </div>
        ))}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button type="button" variant="outline" size="sm" data-testid="add-ppf-roll-btn" onClick={() => set("rolls", [...p.rolls, { product: p.rolls.at(-1)?.product || "", serial_id: "", sqft: "" }])}>
            <Plus className="mr-1 h-3.5 w-3.5" />Add another product / roll
          </Button>
          <span data-testid="ppf-total-sqft" className="text-sm font-bold text-brand-ink">Total {total} sqft</span>
        </div>
      </div>
    </Section>
  );
};

const PlanFields = ({ f, setF, canPickBrand }) => {
  const set = (k, v) => setF({ ...f, plan: { ...f.plan, [k]: v } });
  return (
    <Section title="Protection Plan & payment to VeloGuardX">
      {canPickBrand && (
        <div className="flex gap-2">
          {[PPF, TINT].map((b) => (
            <button key={b} type="button" data-testid={`plan-brand-${b.split(" ")[0].toLowerCase()}`} onClick={() => setF({ ...f, planBrand: b })}
              className={`min-h-[40px] rounded-full px-4 text-sm font-semibold ${f.planBrand === b ? "bg-brand-deep text-white" : "bg-slate-100 text-slate-600"}`}>Plan on {b}</button>
          ))}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Plan type"><Input data-testid="plan-type" value={f.plan.plan_type} readOnly className="bg-slate-50" /></Field>
        <Field label="Payment mode *">
          <Select value={f.plan.payment_mode} onValueChange={(v) => set("payment_mode", v)}>
            <SelectTrigger data-testid="payment-mode"><SelectValue placeholder="Choose" /></SelectTrigger>
            <SelectContent>{["UPI", "Bank transfer", "Cash", "Cheque"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Reference"><Input data-testid="payment-ref" value={f.plan.payment_reference} onChange={(e) => set("payment_reference", e.target.value)} /></Field>
      </div>
      <PhotoUploader label="Payment proof" testid="upload-proof" value={f.plan.payment_proof} onChange={(v) => set("payment_proof", v)} max={2} />
    </Section>
  );
};

const Tick = ({ on, label, onClick, disabled, testid }) => (
  <button type="button" data-testid={testid} disabled={disabled} onClick={onClick}
    className={`flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border-2 px-3 text-sm font-semibold transition-colors ${on ? "border-crimson-500 bg-crimson-50 text-crimson-700" : "border-slate-200 bg-white text-slate-500"} disabled:opacity-60`}>
    <span className={`flex h-5 w-5 items-center justify-center rounded border-2 text-xs ${on ? "border-crimson-500 bg-crimson-500 text-white" : "border-slate-300"}`}>{on ? "✓" : ""}</span>{label}
  </button>
);

const CustomerPick = ({ onPick }) => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get(`${P}/customers`).then(({ data }) => setList(data)); }, []);
  if (!list.length) return null;
  return (
    <Section title="Choose from customer list">
      <select data-testid="reg-customer-pick" defaultValue="" onChange={(e) => { const c = list.find((x) => x.id === e.target.value); if (c) onPick(c); }}
        className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm">
        <option value="">Pick a customer to fill name, mobile, email and vehicle…</option>
        {list.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.mobile}{c.reg_no ? ` · ${c.reg_no}` : ""}</option>)}
      </select>
    </Section>
  );
};

const payloads = (f) => {
  const base = { customer: f.customer, vehicle: f.vehicle, install_date: f.install_date, installer_name: f.installer_name, photos: f.photos, inspection_id: f.inspection_id };
  const ppf = { ...base, brand: PPF, product: f.ppf.product, coverage: f.ppf.coverage, coverage_panels: f.ppf.coverage_panels,
    rolls: f.ppf.rolls.map((r) => ({ product: r.product || "", serial_id: r.serial_id || null, sqft: Number(r.sqft || 0) })) };
  const tint = { ...base, brand: TINT, films: f.films.map((x) => ({ ...x, sqft: Number(x.sqft || 0), serial_id: x.serial_id || null })) };
  const plan = { ...(f.planBrand === TINT ? tint : ppf), ...f.plan };
  return { ppf, tint, plan };
};

export const RegistrationForm = ({ initial, editing, studioEmail, onDone }) => {
  const [f, setF] = useState(initial);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`${P}/products`).then(({ data }) => setProducts(data)); }, []);
  const set = (grp, k, v) => setF({ ...f, [grp]: { ...f[grp], [k]: v } });
  const on = f.on;
  const planBrand = on.ppf ? PPF : on.tint ? TINT : f.planBrand;
  const showPpf = on.ppf || (on.plan && planBrand === PPF);
  const showTint = on.tint || (on.plan && planBrand === TINT);
  const credits = {};
  (editing?.deductions || []).forEach((d) => { credits[d.serial_id] = { id: d.serial_id, sqft: (credits[d.serial_id]?.sqft || 0) + d.sqft }; });
  const toggle = (k) => setF({ ...f, on: { ...on, [k]: !on[k] } });
  const submit = async () => {
    setBusy(true);
    const p = payloads({ ...f, planBrand });
    try {
      if (editing) {
        const body = editing.kind === "plan" ? p.plan : editing.brand === TINT ? p.tint : p.ppf;
        await api.put(`${P}/${editing.kind === "plan" ? "plans" : "warranties"}/${editing.id}`, body);
        toast.success("Resubmitted");
      } else {
        const { data } = await api.post(`${P}/registrations/bundle`, { ppf: on.ppf ? p.ppf : null, velotint: on.tint ? p.tint : null, plan: on.plan ? p.plan : null });
        toast.success(`${data.submissions.length} registration${data.submissions.length > 1 ? "s" : ""} submitted to VeloGuardX`);
      }
      onDone(editing ? null : f);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      {!editing && (
        <Section title="What are you registering? Tick all that apply">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Tick on={on.ppf} label="PPF Warranty" onClick={() => toggle("ppf")} testid="tick-ppf" />
            <Tick on={on.tint} label="Velotint Warranty" onClick={() => toggle("tint")} testid="tick-velotint" />
            <Tick on={on.plan} label="Protection Plan" onClick={() => toggle("plan")} testid="tick-plan" />
          </div>
          <p className="text-xs text-slate-500">Tick PPF Warranty, Velotint Warranty and Protection Plan together — the customer and vehicle details below are entered once and used for all of them.</p>
        </Section>
      )}
      <CustomerPick onPick={(c) => setF({ ...f,
        customer: { ...f.customer, name: c.name, mobile: c.mobile, email: c.email || f.customer.email },
        vehicle: { ...f.vehicle, make: c.brand || f.vehicle.make, model: c.model || f.vehicle.model, reg_no: c.reg_no || f.vehicle.reg_no } })} />
      <CustomerFields f={f} set={set} />
      <VehicleFields f={f} set={set} editingId={editing?.id} />
      <Section title="Installation">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Installation date *"><Input data-testid="install-date" type="date" value={f.install_date} onChange={(e) => setF({ ...f, install_date: e.target.value })} /></Field>
          <Field label="Installer name *"><Input data-testid="installer-name" value={f.installer_name} onChange={(e) => setF({ ...f, installer_name: e.target.value })} /></Field>
          <Field label="Studio email (auto)"><Input data-testid="studio-email" value={studioEmail} readOnly className="bg-slate-50" /></Field>
        </div>
      </Section>
      {showPpf && <PpfFields f={f} setF={setF} products={products} needSerial={on.ppf} credits={credits} />}
      {showTint && <VelotintFilms films={f.films} setFilms={(films) => setF({ ...f, films })} products={products} needSerial={on.tint} credits={credits} />}
      <Section title="Photos">
        <div className="grid gap-4 sm:grid-cols-3">
          {["before", "after", "invoice"].map((k) => (
            <PhotoUploader key={k} label={k === "invoice" ? "Invoice (optional)" : `${k[0].toUpperCase()}${k.slice(1)}`} testid={`upload-${k}`}
              value={f.photos[k]} onChange={(v) => setF({ ...f, photos: { ...f.photos, [k]: v } })} />
          ))}
        </div>
      </Section>
      {on.plan && <PlanFields f={{ ...f, planBrand }} setF={setF} canPickBrand={!editing && !on.ppf && !on.tint} />}
      <Button data-testid="submit-registration-btn" disabled={busy || !(on.ppf || on.tint || on.plan)} onClick={submit}
        className="min-h-[48px] w-full bg-crimson-500 text-base hover:bg-crimson-600 sm:w-auto sm:px-10">
        {editing ? "Resubmit" : "Submit"}
      </Button>
    </div>
  );
};
